const heicToPng = async (file: File, fileName: string): Promise<File> => {
    const { heicTo } = await import('heic-to');
    const converted = await heicTo({
        blob: file,
        type: 'image/png',
    });

    const convertedBlob = Array.isArray(converted) ? converted[0] : converted;

    const result = new File([convertedBlob], `${fileName}`, {
        type: 'image/png',
    });

    return result;
};

export const convertImage = (file: File, ext: string): Promise<File> => {
    const fileName = file.name.toLocaleLowerCase().replace(`.${ext}`, '');

    return new Promise((resolve, reject) => {
        if (ext === 'heic') {
            heicToPng(file, fileName).then((result: File) => resolve(result));
        } else {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    return reject();
                }

                ctx.drawImage(img, 0, 0);

                canvas.toBlob((blob) => {
                    if (!blob) {
                        return reject();
                    }

                    const result = new File([blob], `${fileName}.png`, {
                        type: 'image/png',
                    });

                    resolve(result);
                }, 'image/png');
            };

            img.onerror = () => {
                return reject();
            };
        }
    });
};

export const resizeImage = (
    file: File,
    ext: string,
    maxWidth: number,
    maxHeight: number,
): Promise<File> => {
    const fileName = file.name.replace(`.${ext}`, '');
    return new Promise((resolve) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = () => {
            let width = img.width;
            let height = img.height;

            if (width <= maxWidth && height <= maxHeight) {
                return resolve(file);
            }

            if (width > height) {
                if (width > maxWidth) {
                    height = height * (maxWidth / width);
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = width * (maxHeight / height);
                    height = maxHeight;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (ctx) {
                ctx.imageSmoothingEnabled = true;
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (!blob) {
                        return;
                    }

                    const result = new File([blob], `${fileName}`, {
                        type: 'image/png',
                    });

                    resolve(result);
                }, 'image/png');
            } else {
                return;
            }
        };

        img.onerror = (err) => {
            return;
        };
    });
};

export const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
                return;
            }

            reject(new Error('Unable to convert file to base64'));
        };
        reader.onerror = () =>
            reject(new Error('Unable to convert file to base64'));

        reader.readAsDataURL(file);
    });

export const base64ToFile = async (
    base64: string,
    filename: string,
): Promise<File> => {
    const response = await fetch(base64);
    const blob = await response.blob();

    return new File([blob], filename, { type: blob.type });
};
